export type TAvailableColumnsBySource = Record<
	string,
	Array<string>
>;

const normalizeColumns = (
	columns: Array<unknown>
): Array<string> => {
	return Array.from(
		new Set(
			columns
				.map((column) => String(column ?? "").trim())
				.filter(Boolean)
		)
	);
};

export const groupAvailableColsBySource = (
	availableCols: unknown
): TAvailableColumnsBySource => {
	if (
		!availableCols ||
		typeof availableCols !== "object" ||
		Array.isArray(availableCols)
	) {
		return {};
	}

	return Object.entries(
		availableCols as Record<string, unknown>
	).reduce<TAvailableColumnsBySource>((acc, [source, cols]) => {
		if (!cols || typeof cols !== "object" || Array.isArray(cols)) {
			return acc;
		}

		const headers = normalizeColumns(
			Object.values(cols as Record<string, unknown>).map(
				(columnInfo) => {
					if (
						columnInfo &&
						typeof columnInfo === "object" &&
						"header" in columnInfo
					) {
						return (
							columnInfo as {
								header?: unknown;
							}
						).header;
					}
					return "";
				}
			)
		);

		if (headers.length > 0) {
			acc[source] = headers;
		}

		return acc;
	}, {});
};

export const flattenAvailableCols = (
	availableCols: unknown
): Array<string> => {
	const grouped = groupAvailableColsBySource(availableCols);
	return normalizeColumns(Object.values(grouped).flat());
};

export const buildFallbackAvailableColsBySource = (
	columns: unknown,
	groupName = "all_columns"
): TAvailableColumnsBySource => {
	if (!Array.isArray(columns)) {
		return {};
	}

	const normalizedColumns = normalizeColumns(columns);
	if (normalizedColumns.length === 0) {
		return {};
	}

	return {
		[groupName]: normalizedColumns,
	};
};
